import type { Dish } from './dish';
import type { InvoiceSelectedOption } from './invoiceNotification';

/** Chỉ số cột cố định trên sheet "Thực đơn" của CUKCUK (DANHMUCTHUCDON) */
export const CUKCUK_MENU_COLUMNS = {
  TYPE: 0,
  CODE: 1,
  BARCODE: 2,
  NAME: 3,
  ALT_NAME: 4,
  UNIT: 5,
  PRICE: 6,
  SPECIAL_FEE_AMOUNT: 7,
  COST_PRICE: 9,
  DESCRIPTION: 13,
  MENU_GROUP: 14,
  DISPLAY_ORDER: 15,
  IS_FEATURED: 16,
  IS_VISIBLE: 17,
  NVL_CODE: 18,
  NVL_NAME: 19,
  NVL_UNIT: 20,
  NVL_QTY: 21,
  STPV_LABEL: 22,
  STPV_EXTRA_PRICE: 23,
  PREP_NAME: 24,
  PREP_UNIT: 25,
  SUB_DISH_CODE: 27,
  SUB_DISH_QTY: 28,
  COMBO_CODE: 29,
  COMBO_QTY: 30,
} as const;

/** Catalog option (Sở thích phục vụ) parsed từ CUKCUK */
export interface MenuOption {
  code: string;
  name: string;
  price: number;
  isActive: boolean;
  unit?: string;
  optionGroupName?: string;
  vat?: number;
  displayOrder?: number;
  description?: string;
  source: CukcukMenuOptionSource;
}

export interface CukcukMenuOptionSource {
  cukcukCode?: string;
  cukcukName?: string;
  cukcukGroupCode?: string;
  cukcukGroupName?: string;
  sheetName?: string;
  rowNumber?: number;
  columnName?: string;
  isHiddenColumn?: boolean;
  rawRow?: Record<string, unknown>;
}

/** Quan hệ món ↔ nhóm tùy chọn */
export interface DishOption {
  dishCode: string;
  dishName?: string;
  groupName: string;
  isRequired: boolean;
  limit: number;
  options: MenuOptionRef[];
  source: CukcukDishOptionSource;
  displayOrder?: number;
}

export interface MenuOptionRef {
  code: string;
  name: string;
  price: number;
  isActive: boolean;
  displayOrder?: number;
}

export interface CukcukDishOptionSource {
  cukcukDishCode?: string;
  cukcukDishName?: string;
  cukcukGroupCode?: string;
  cukcukGroupName?: string;
  cukcukOptionCodes?: string[];
  sheetName?: string;
  rowNumber?: number;
  columnName?: string;
  isHiddenColumn?: boolean;
  rawValue?: unknown;
  rawRow?: Record<string, unknown>;
}

export interface CukcukIngredientRef {
  code: string;
  name?: string;
  unit?: string;
  quantity?: string | number;
}

/** Món chính sau parse — tương thích Dish, kèm metadata nguồn */
export interface ParsedCukcukDish {
  dish: Partial<Dish>;
  source: CukcukDishSource;
}

export interface CukcukDishSource {
  sheetName: string;
  rowNumber: number;
  rawRow: Record<string, unknown>;
  cukcukType?: string;
  ingredients?: CukcukIngredientRef[];
}

export interface CukcukParseResult {
  dishes: ParsedCukcukDish[];
  options: MenuOption[];
  dishOptions: DishOption[];
  meta: CukcukParseMeta;
}

export interface CukcukParseMeta {
  fileName: string;
  sheetNames: string[];
  menuSheetName?: string;
  optionSheetName?: string;
  parserVersion: string;
  warnings: string[];
  hiddenColumnsUsed: string[];
  headerRowIndex?: number;
  dataStartRowIndex?: number;
}

/** Một dòng STPV trên sheet CUKCUK */
export interface CukcukStpvRow {
  label: string;
  extraPrice: number;
  rowNumber: number;
  isHiddenColumn: boolean;
}

/** Chỉ số cột sheet chi tiết doanh thu theo hóa đơn CUKCUK */
export const CUKCUK_INVOICE_COLUMNS = {
  SALE_DATE: 0,
  TIME_RANGE: 1,
  INVOICE_CODE: 2,
  ITEM_CODE: 3,
  ITEM_NAME: 4,
  UNIT: 5,
  QUANTITY: 6,
  UNIT_PRICE: 7,
  LINE_TOTAL: 8,
  MERCHANDISE_AMOUNT: 9,
  FEE_AMOUNT: 10,
  VAT_AMOUNT: 11,
  DISCOUNT_AMOUNT: 12,
  ROUNDING_AMOUNT: 13,
  DELIVERY_DISCOUNT: 14,
  VAT_RATE: 15,
  ITEM_TYPE: 16,
  MENU_GROUP: 17,
  CUSTOMER_NAME: 18,
  GUEST_COUNT: 19,
  SERVICE_MODE: 20,
  AREA: 21,
  TABLE_NAME: 22,
  SHIFT_NAME: 23,
  CASHIER_NAME: 24,
  SERVER_NAME: 25,
} as const;

/** Một dòng món chính trên hóa đơn CUKCUK */
export interface CukcukInvoiceLineItem {
  itemId: string;
  itemName: string;
  quantity: number;
  unit?: string;
  unitPrice: number;
  lineTotal: number;
  itemGroup?: string;
  itemType?: string;
  vatRate?: string;
  selectedOptions: InvoiceSelectedOption[];
  sourceRowNumber: number;
}

/** Hóa đơn CUKCUK đã gom dòng */
export interface CukcukInvoice {
  invoiceCode: string;
  saleDate?: string;
  checkInTime?: string;
  checkOutTime?: string;
  totalAmount?: number;
  merchandiseAmount?: number;
  feeAmount?: number;
  vatAmount?: number;
  discountAmount?: number;
  guestCount?: number;
  customerName?: string;
  serviceMode?: string;
  area?: string;
  tableName?: string;
  shiftName?: string;
  cashierName?: string;
  serverName?: string;
  items: CukcukInvoiceLineItem[];
}

export interface CukcukInvoiceParseMeta {
  fileName: string;
  sheetNames: string[];
  sheetName?: string;
  parserVersion: string;
  warnings: string[];
  headerRowIndex?: number;
  dataStartRowIndex?: number;
  totalInvoices: number;
  totalLineItems: number;
  totalOptions: number;
}

export interface CukcukInvoiceParseResult {
  invoices: CukcukInvoice[];
  meta: CukcukInvoiceParseMeta;
}

/** Block món gồm dòng chính + các dòng nối tiếp (NVL / STPV) */
export interface CukcukDishBlock {
  code: string;
  name: string;
  cukcukType: string;
  unit: string;
  price: number;
  barcode?: string;
  description?: string;
  menuGroup?: string;
  displayOrder?: string;
  isVisible: boolean;
  startRowNumber: number;
  ingredients: CukcukIngredientRef[];
  stpvRows: CukcukStpvRow[];
  subDishCodes: string[];
  comboCodes: string[];
  rawMainRow: Record<string, unknown>;
}